import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Copy, Info, Pencil, Pin, Reply, Trash2, X } from 'lucide-react';
import { MESSAGE_REACTION_EMOJIS } from './messengerThreadUtils';

const REACTION_TEST_IDS = {
  '❤️': 'heart',
  '😂': 'laugh',
  '😮': 'amazed',
  '😢': 'cry',
  '😡': 'angry',
  '👍': 'thumbs-up',
};

const rowStyle = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  border: 'none',
  borderRadius: 18,
  background: 'rgba(248,245,255,0.92)',
  padding: '12px 14px',
  cursor: 'pointer',
  fontFamily: "'Inria Serif', serif",
  fontSize: 17,
  color: '#2C2256',
};

export const MessengerMessageActionSheet = ({
  open,
  message,
  isMine,
  onClose,
  onReact,
  onCopy,
  onInfo,
  onPin,
  onReply,
  onEdit,
  onUnsend,
}) => {
  // Edit is only offered on the sender's own plain-text bubble (matches
  // WhatsApp / Messenger). Not shown on media, on unsent messages, or on
  // remote bubbles.
  const canEdit = Boolean(
    isMine
    && onEdit
    && message
    && (message.message_type || 'text') === 'text'
    && !(message.metadata || {}).unsent,
  );

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key="message-action-sheet"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="message-action-sheet"
          onClick={onClose}
          style={{ position: 'fixed', inset: 0, zIndex: 14000, background: 'rgba(19, 17, 28, 0.24)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '18px 14px calc(18px + env(safe-area-inset-bottom, 0px))' }}
        >
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="premium-card"
            onClick={(event) => event.stopPropagation()}
            style={{ width: '100%', maxWidth: 362, borderRadius: 28, padding: '14px 14px 12px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
              <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{isMine ? 'Message options' : 'React or manage'}</p>
              <button type="button" data-testid="message-action-sheet-close" onClick={onClose} style={{ width: 34, height: 34, borderRadius: '50%', border: '1px solid rgba(112, 63, 246, 0.12)', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <X size={16} color="#2E274B" />
              </button>
            </div>

            {!isMine ? (
              <div data-testid="message-action-sheet-reactions" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8, marginBottom: 12 }}>
                {MESSAGE_REACTION_EMOJIS.map((emoji) => (
                  <button key={emoji} type="button" data-testid={`message-react-${REACTION_TEST_IDS[emoji]}-button`} onClick={() => onReact(emoji)} style={{ height: 46, borderRadius: 999, border: '1px solid rgba(112, 63, 246, 0.10)', background: 'rgba(248, 245, 255, 0.9)', fontSize: 26, cursor: 'pointer' }}>
                    {emoji}
                  </button>
                ))}
              </div>
            ) : null}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
              <button type="button" data-testid="message-action-reply" onClick={() => onReply(message)} style={rowStyle}>
                <Reply size={18} /> Reply
              </button>
              {canEdit ? (
                <button type="button" data-testid="message-action-edit" onClick={() => onEdit(message)} style={rowStyle}>
                  <Pencil size={18} /> Edit message
                </button>
              ) : null}
              <button type="button" data-testid="message-action-info" onClick={() => onInfo(message)} style={rowStyle}>
                <Info size={18} /> Message info
              </button>
              {!isMine ? (
                <button type="button" data-testid="message-action-pin" onClick={() => onPin(message)} style={rowStyle}>
                  <Pin size={18} /> Pin message
                </button>
              ) : null}
              <button type="button" data-testid="message-action-copy" onClick={() => onCopy(message)} style={rowStyle}>
                <Copy size={18} /> Copy
              </button>
              {isMine ? (
                <button type="button" data-testid="message-action-unsend" onClick={() => onUnsend(message)} style={{ ...rowStyle, background: 'rgba(255, 241, 244, 0.94)', color: '#A93A55' }}>
                  <Trash2 size={18} /> Unsend
                </button>
              ) : null}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};
